// Enhanced Contact Form Module with Advanced Tracking
// Optimized for Lambda Backend with Comprehensive Analytics
import { siteConfig, domainContent } from '../config.js';
import analytics from './analytics.js';

export class ContactForm {
    constructor() {
        this.domainData = null;
        this.formElement = null;
        this.isSubmitting = false;
        this.rateLimitCount = 0;
        this.lastSubmitTime = 0;
        
        // Enhanced tracking properties
        this.formStartTime = null;
        this.fieldInteractions = new Map();
        this.lastInteractedField = null;
        this.formStarted = false;
        this.abandonmentTimer = null;
        this.validationAttempts = new Map();
        
        // Use environment variable for Lambda endpoint
        this.lambdaUrl = siteConfig.global.lambdaUrl;
        this.maxRetries = 2;
        this.retryDelay = 1000;
        
        // Performance optimization
        this.debounceTimers = new Map();
        this.fieldCache = new Map();
    }

    init(domainData) {
        this.domainData = domainData;
        this.formElement = document.getElementById('contactForm');
        
        if (this.formElement) {
            this.setupEventListeners();
            this.setupFieldValidation();
            this.setupAutoSave();
            this.addHoneypotField();
            this.setupEnhancedTracking();
            this.setupAccessibility();
            this.trackFormView();
        }
    }

    // Enhanced tracking setup
    setupEnhancedTracking() {
        // Track form abandonment on page unload
        window.addEventListener('beforeunload', () => this.trackAbandonment());
        
        // Track visibility changes (tab switching)
        document.addEventListener('visibilitychange', () => {
            if (document.hidden && this.formStarted && !this.isSubmitting) {
                this.trackEvent('FormBackgrounded', {
                    progress: this.getFormProgress(),
                    timeSpent: this.getTimeSpent()
                });
            }
        });
        
        // Set up idle detection
        this.setupIdleDetection();
    }

    trackFormView() {
        this.trackEvent('FormView', {
            domain: window.location.hostname,
            referrer: document.referrer,
            device: this.getDeviceType(),
            viewport: `${window.innerWidth}x${window.innerHeight}`
        });
    }

    trackFormStart() {
        if (!this.formStarted) {
            this.formStarted = true;
            this.formStartTime = Date.now();
            
            this.trackEvent('FormStart', {
                domain: window.location.hostname,
                device: this.getDeviceType(),
                entryField: this.lastInteractedField
            });
        }
    }

    setupIdleDetection() {
        let idleTimer = null;
        const IDLE_THRESHOLD = 30000; // 30 seconds
        
        const resetIdleTimer = () => {
            clearTimeout(idleTimer);
            idleTimer = setTimeout(() => {
                if (this.formStarted && !this.isSubmitting) {
                    this.trackEvent('FormIdle', {
                        lastField: this.lastInteractedField,
                        progress: this.getFormProgress(),
                        timeBeforeIdle: this.getTimeSpent()
                    });
                }
            }, IDLE_THRESHOLD);
        };
        
        // Reset timer on any interaction
        ['mousedown', 'keypress', 'scroll', 'touchstart'].forEach(event => {
            document.addEventListener(event, resetIdleTimer, true);
        });
        
        resetIdleTimer();
    }

    addHoneypotField() {
        // Add honeypot field for bot detection
        const honeypot = document.createElement('input');
        honeypot.type = 'text';
        honeypot.name = 'website';
        honeypot.tabIndex = -1;
        honeypot.autocomplete = 'off';
        honeypot.style.cssText = 'position:absolute;left:-9999px;opacity:0;pointer-events:none;';
        honeypot.setAttribute('aria-hidden', 'true');
        this.formElement.appendChild(honeypot);
    }

    setupAccessibility() {
        // Add ARIA labels and descriptions
        const submitBtn = this.formElement.querySelector('[type="submit"]');
        if (submitBtn) {
            submitBtn.setAttribute('aria-label', 'Submit contact form');
        }
        
        // Add form description
        this.formElement.setAttribute('aria-label', 'Contact form for domain inquiry');
        
        // Announce form errors to screen readers
        this.errorAnnouncer = document.createElement('div');
        this.errorAnnouncer.setAttribute('role', 'alert');
        this.errorAnnouncer.setAttribute('aria-live', 'polite');
        this.errorAnnouncer.className = 'sr-only';
        this.formElement.appendChild(this.errorAnnouncer);
    }

    setupEventListeners() {
        // Form submission
        this.formElement.addEventListener('submit', (e) => this.handleSubmit(e));
        
        // Track form start on first interaction
        this.formElement.addEventListener('focusin', () => this.trackFormStart(), { once: true });
        
        // Field interactions
        const fields = this.formElement.querySelectorAll('input:not([name="website"]), textarea');
        fields.forEach(field => {
            // Focus tracking with timing
            field.addEventListener('focus', () => this.handleFieldFocus(field));
            
            // Blur tracking with validation
            field.addEventListener('blur', () => this.handleFieldBlur(field));
            
            // Real-time validation with debouncing
            field.addEventListener('input', (e) => this.handleFieldInput(e, field));
            
            // Track paste events
            field.addEventListener('paste', () => this.trackFieldPaste(field));
            
            // Character count for textarea
            if (field.tagName === 'TEXTAREA') {
                this.setupCharacterCount(field);
            }
            
            // Initialize field data
            this.initializeFieldData(field);
        });
        
        // Prevent double submission
        this.formElement.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' && e.ctrlKey && !this.isSubmitting) {
                e.preventDefault();
                this.formElement.requestSubmit();
            }
        });
    }

    initializeFieldData(field) {
        this.fieldInteractions.set(field.name, {
            focusCount: 0,
            blurCount: 0,
            totalTime: 0,
            lastFocusTime: null,
            errors: 0,
            corrections: 0,
            completed: false,
            hasInput: false,
            pasteCount: 0,
            maxLength: 0
        });
    }

    handleFieldFocus(field) {
        const fieldData = this.fieldInteractions.get(field.name);
        if (fieldData) {
            fieldData.focusCount++;
            fieldData.lastFocusTime = Date.now();
            this.lastInteractedField = field.name;
            
            // Track focus event
            this.trackFieldEvent('FieldFocus', field, {
                focusCount: fieldData.focusCount,
                returningToField: fieldData.focusCount > 1
            });
            
            // Track potential confusion (multiple focuses)
            if (fieldData.focusCount > 2 && !fieldData.completed) {
                this.trackFieldEvent('FieldConfusion', field, {
                    focusCount: fieldData.focusCount
                });
            }
        }
        
        // Add visual feedback
        field.parentElement?.classList.add('field-focused');
    }

    handleFieldBlur(field) {
        const fieldData = this.fieldInteractions.get(field.name);
        if (fieldData && fieldData.lastFocusTime) {
            const timeSpent = Date.now() - fieldData.lastFocusTime;
            fieldData.totalTime += timeSpent;
            fieldData.blurCount++;
            
            // Update completion status
            const wasCompleted = fieldData.completed;
            fieldData.completed = this.isFieldCompleted(field);
            
            // Track completion if newly completed
            if (!wasCompleted && fieldData.completed) {
                this.trackFieldEvent('FieldComplete', field, {
                    timeSpent: Math.round(fieldData.totalTime / 1000),
                    attempts: fieldData.focusCount
                });
            }
            
            // Track difficulty if took too long
            if (timeSpent > 30000) { // 30 seconds
                this.trackFieldEvent('FieldDifficulty', field, {
                    timeSpentSeconds: Math.round(timeSpent / 1000)
                });
            }
        }
        
        // Validate on blur
        this.validateField(field);
        
        // Remove visual feedback
        field.parentElement?.classList.remove('field-focused');
    }

    handleFieldInput(e, field) {
        const fieldData = this.fieldInteractions.get(field.name);
        if (fieldData) {
            fieldData.hasInput = true;
            fieldData.maxLength = Math.max(fieldData.maxLength, field.value.length);
        }
        
        // Debounced validation
        this.debounce(`validate-${field.name}`, () => {
            this.validateField(field);
        }, 500);
        
        // Clear error state on input
        if (field.classList.contains('error')) {
            this.clearFieldError(field);
        }
    }

    trackFieldPaste(field) {
        const fieldData = this.fieldInteractions.get(field.name);
        if (fieldData) {
            fieldData.pasteCount++;
            
            this.trackFieldEvent('FieldPaste', field, {
                pasteCount: fieldData.pasteCount
            });
        }
    }

    isFieldCompleted(field) {
        const value = field.value.trim();
        
        // Check if field has meaningful content
        if (field.type === 'email') {
            return value.length > 0 && field.validity.valid;
        } else if (field.type === 'tel') {
            return value.length >= 10; // Minimum phone length
        } else {
            return value.length >= (field.minLength || 2);
        }
    }

    setupFieldValidation() {
        const fields = this.formElement.querySelectorAll('[required]');
        
        fields.forEach(field => {
            field.addEventListener('invalid', (e) => {
                e.preventDefault();
                const error = this.getFieldError(field);
                this.showFieldError(field, error);
                
                // Track validation error
                this.trackValidationError(field, error);
            });
        });
    }

    trackValidationError(field, error) {
        const attempts = this.validationAttempts.get(field.name) || 0;
        this.validationAttempts.set(field.name, attempts + 1);
        
        const fieldData = this.fieldInteractions.get(field.name);
        if (fieldData) {
            fieldData.errors++;
        }
        
        this.trackFieldEvent('ValidationError', field, {
            errorType: this.getValidationErrorType(field),
            errorMessage: error,
            attemptNumber: attempts + 1,
            fieldLength: field.value.length
        });
    }

    getValidationErrorType(field) {
        if (field.validity.valueMissing) return 'required';
        if (field.validity.typeMismatch) return 'type';
        if (field.validity.patternMismatch) return 'pattern';
        if (field.validity.tooShort) return 'minLength';
        if (field.validity.tooLong) return 'maxLength';
        if (field.validity.rangeUnderflow) return 'min';
        if (field.validity.rangeOverflow) return 'max';
        if (field.validity.stepMismatch) return 'step';
        if (field.validity.customError) return 'custom';
        return 'unknown';
    }

    setupCharacterCount(textarea) {
        const maxLength = 5000;
        const wrapper = textarea.parentElement;
        
        // Create counter element
        const counter = document.createElement('div');
        counter.className = 'character-counter';
        counter.setAttribute('aria-live', 'polite');
        counter.setAttribute('aria-atomic', 'true');
        wrapper.appendChild(counter);
        
        const updateCounter = () => {
            const length = textarea.value.length;
            const remaining = maxLength - length;
            
            counter.textContent = `${length} / ${maxLength}`;
            
            // Update counter state
            counter.classList.toggle('warning', remaining < 500);
            counter.classList.toggle('error', remaining < 100);
            
            // Announce to screen readers when getting close to limit
            if (remaining === 100) {
                this.errorAnnouncer.textContent = '100 characters remaining';
            } else if (remaining === 0) {
                this.errorAnnouncer.textContent = 'Character limit reached';
            }
            
            // Track when user writes long messages
            if (length > 1000 && length % 500 === 0) {
                this.trackEvent('LongMessage', {
                    length,
                    field: textarea.name
                });
            }
        };
        
        textarea.addEventListener('input', updateCounter);
        updateCounter(); // Initialize
    }

    setupAutoSave() {
        const AUTOSAVE_KEY = 'contactFormDraft';
        const AUTOSAVE_INTERVAL = 10000; // 10 seconds
        
        // Load saved data
        this.loadAutoSave();
        
        // Save periodically
        setInterval(() => {
            if (this.hasFormData() && !this.isSubmitting) {
                this.saveFormData();
            }
        }, AUTOSAVE_INTERVAL);
        
        // Save on significant events
        ['blur', 'change'].forEach(event => {
            this.formElement.addEventListener(event, () => {
                this.debounce('autosave', () => this.saveFormData(), 1000);
            }, true);
        });
    }

    loadAutoSave() {
        try {
            const saved = localStorage.getItem('contactFormDraft');
            if (saved) {
                const data = JSON.parse(saved);
                const age = Date.now() - data.timestamp;
                
                // Only restore if less than 24 hours old
                if (age < 86400000) {
                    this.restoreFormData(data.fields);
                    this.showAutoSaveNotification();
                } else {
                    localStorage.removeItem('contactFormDraft');
                }
            }
        } catch (error) {
            console.error('Error loading autosave:', error);
        }
    }

    saveFormData() {
        try {
            const formData = this.getFormData();
            const draft = {
                fields: formData,
                timestamp: Date.now(),
                domain: this.domainData?.title || window.location.hostname
            };
            
            localStorage.setItem('contactFormDraft', JSON.stringify(draft));
        } catch (error) {
            console.error('Error saving form data:', error);
        }
    }

    restoreFormData(data) {
        Object.entries(data).forEach(([name, value]) => {
            const field = this.formElement.querySelector(`[name="${name}"]`);
            if (field && field.name !== 'website') { // Skip honeypot
                field.value = value;
                field.dispatchEvent(new Event('input', { bubbles: true }));
            }
        });
    }

    showAutoSaveNotification() {
        const notification = document.createElement('div');
        notification.className = 'autosave-notification';
        notification.textContent = 'Form data restored from your previous session';
        notification.setAttribute('role', 'status');
        
        this.formElement.insertBefore(notification, this.formElement.firstChild);
        
        setTimeout(() => {
            notification.classList.add('fade-out');
            setTimeout(() => notification.remove(), 300);
        }, 3000);
    }

    async handleSubmit(event) {
        event.preventDefault();
        
        if (this.isSubmitting) {
            return;
        }
        
        // Check rate limiting
        if (!this.checkRateLimit()) {
            this.showError('Please wait a moment before submitting again.');
            this.trackEvent('RateLimitTriggered', {
                attempts: this.rateLimitCount
            });
            return;
        }
        
        // Validate honeypot
        const honeypot = this.formElement.querySelector('[name="website"]');
        if (honeypot && honeypot.value) {
            // Bot detected - silently fail
            this.trackEvent('BotDetected', {
                honeypotValue: honeypot.value.substring(0, 20)
            });
            this.showSuccess('Thank you for your inquiry. We will get back to you soon.');
            return;
        }
        
        // Track submission attempt
        const formData = this.getFormData();
        await this.trackFormSubmission(formData);
        
        this.isSubmitting = true;
        this.showLoadingState();
        
        try {
            const enrichedData = this.enrichFormData(formData);
            const response = await this.submitToLambda(enrichedData);
            
            this.handleSubmitSuccess(response);
        } catch (error) {
            this.handleSubmitError(error);
        } finally {
            this.isSubmitting = false;
            this.hideLoadingState();
        }
    }

    async trackFormSubmission(formData) {
        const completionData = this.getFormCompletionData();
        
        await this.trackEvent('FormSubmit', {
            domain: this.domainData?.title || window.location.hostname,
            progress: completionData.progress,
            timeToSubmit: this.getTimeSpent(),
            fieldInteractions: completionData.totalInteractions,
            device: this.getDeviceType()
        });
    }

    enrichFormData(formData) {
        return {
            ...formData,
            domain: this.domainData?.title || window.location.hostname,
            timestamp: new Date().toISOString(),
            metadata: {
                userAgent: navigator.userAgent,
                language: navigator.language,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                screenResolution: `${window.screen.width}x${window.screen.height}`,
                formVersion: '2.0'
            }
        };
    }

    async submitToLambda(formData) {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 30000); // 30s timeout
        
        try {
            const response = await fetch(this.lambdaUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Domain': window.location.hostname,
                    'X-Form-Version': '2.0'
                },
                body: JSON.stringify(formData),
                signal: controller.signal
            });
            
            clearTimeout(timeout);
            
            const data = await response.json();
            
            if (!response.ok) {
                throw {
                    type: data.error?.type || 'API_ERROR',
                    message: data.error?.message || 'Submission failed',
                    details: data.error?.details,
                    status: response.status
                };
            }
            
            return data;
        } catch (error) {
            clearTimeout(timeout);
            
            if (error.name === 'AbortError') {
                throw {
                    type: 'TIMEOUT_ERROR',
                    message: 'Request timed out. Please try again.',
                    retry: true
                };
            }
            
            throw error;
        }
    }

    handleSubmitSuccess(response) {
        // Clear form and autosave
        this.formElement.reset();
        localStorage.removeItem('contactFormDraft');
        
        // Track success with enhanced data
        const completionData = this.getFormCompletionData();
        this.trackEvent('FormSuccess', {
            inquiryId: response.inquiryId,
            processingTime: response.processingTime,
            totalTime: this.getTimeSpent(),
            fieldMetrics: completionData
        });
        
        // Show success message
        this.showSuccess(
            response.message || 'Thank you for your inquiry. We will get back to you within 24 hours.'
        );
        
        // Disable form to prevent resubmission
        this.disableForm();
        
        // Optional: Redirect after delay
        if (this.domainData?.successRedirect) {
            setTimeout(() => {
                window.location.href = this.domainData.successRedirect;
            }, 3000);
        }
    }

    handleSubmitError(error) {
        console.error('Form submission error:', error);
        
        // Track error with details
        this.trackEvent('FormError', {
            errorType: error.type || 'Unknown',
            errorMessage: error.message,
            errorStatus: error.status,
            retriable: error.retry || false
        });
        
        // Show appropriate error message
        if (error.details && typeof error.details === 'object') {
            // Field-specific errors
            Object.entries(error.details).forEach(([field, message]) => {
                const fieldElement = this.formElement.querySelector(`[name="${field}"]`);
                if (fieldElement) {
                    this.showFieldError(fieldElement, message);
                }
            });
            this.showError('Please correct the highlighted fields and try again.');
        } else {
            // General error
            this.showError(error.message || 'Something went wrong. Please try again.');
        }
        
        // Offer retry for network errors
        if (error.retry) {
            this.showRetryOption();
        }
    }

    showRetryOption() {
        const retryBtn = document.createElement('button');
        retryBtn.className = 'retry-btn';
        retryBtn.textContent = 'Retry';
        retryBtn.onclick = () => {
            this.formElement.requestSubmit();
        };
        
        const errorContainer = this.formElement.querySelector('.error-message');
        if (errorContainer) {
            errorContainer.appendChild(retryBtn);
        }
    }

    // Tracking helper methods
    trackEvent(action, data = {}) {
        if (window.analytics?.trackEvent) {
            window.analytics.trackEvent('Contact', action, data);
        }
    }

    trackFieldEvent(action, field, data = {}) {
        this.trackEvent(action, {
            field: field.name,
            fieldType: field.type,
            ...data
        });
    }

    trackAbandonment() {
        if (!this.formStarted || this.isSubmitting || this.formElement.dataset.submitted === 'true') {
            return;
        }
        
        const data = this.getFormCompletionData();
        
        if (data.progress > 0 && data.progress < 100) {
            this.trackEvent('FormAbandoned', {
                progress: data.progress,
                lastField: this.lastInteractedField,
                timeSpent: this.getTimeSpent(),
                fieldsWithErrors: data.fieldsWithErrors,
                device: this.getDeviceType()
            });
        }
    }

    getFormCompletionData() {
        const fields = Array.from(this.fieldInteractions.entries());
        const totalFields = fields.filter(([name]) => {
            const field = this.formElement.querySelector(`[name="${name}"]`);
            return field && field.type !== 'hidden';
        }).length;
        
        const completedFields = fields.filter(([_, data]) => data.completed).length;
        const fieldsWithInput = fields.filter(([_, data]) => data.hasInput).length;
        const fieldsWithErrors = fields.filter(([_, data]) => data.errors > 0).length;
        const totalInteractions = fields.reduce((sum, [_, data]) => sum + data.focusCount, 0);
        const totalTime = fields.reduce((sum, [_, data]) => sum + data.totalTime, 0);
        
        const progress = totalFields > 0 ? Math.round((completedFields / totalFields) * 100) : 0;
        
        return {
            progress,
            completedFields,
            totalFields,
            fieldsWithInput,
            fieldsWithErrors: fieldsWithErrors.length,
            totalInteractions,
            totalTimeSpent: Math.round(totalTime / 1000),
            averageTimePerField: completedFields > 0 ? Math.round(totalTime / completedFields / 1000) : 0,
            fieldDetails: Object.fromEntries(fields)
        };
    }

    getTimeSpent() {
        return this.formStartTime ? Math.round((Date.now() - this.formStartTime) / 1000) : 0;
    }

    getDeviceType() {
        const width = window.innerWidth;
        if (width < 768) return 'mobile';
        if (width < 1024) return 'tablet';
        return 'desktop';
    }

    // UI Helper methods
    showLoadingState() {
        const submitBtn = this.formElement.querySelector('[type="submit"]');
        if (submitBtn) {
            submitBtn.disabled = true;
            submitBtn.dataset.originalText = submitBtn.textContent;
            submitBtn.innerHTML = '<span class="spinner"></span> Sending...';
            submitBtn.classList.add('loading');
        }
        
        // Disable all fields
        this.formElement.querySelectorAll('input, textarea, select').forEach(field => {
            field.disabled = true;
        });
    }

    hideLoadingState() {
        const submitBtn = this.formElement.querySelector('[type="submit"]');
        if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.textContent = submitBtn.dataset.originalText || 'Send Message';
            submitBtn.classList.remove('loading');
        }
        
        // Re-enable fields (except honeypot)
        this.formElement.querySelectorAll('input:not([name="website"]), textarea, select').forEach(field => {
            field.disabled = false;
        });
    }

    showSuccess(message) {
        this.removeMessages();
        
        const success = document.createElement('div');
        success.className = 'success-message';
        success.setAttribute('role', 'alert');
        success.innerHTML = `
            <svg class="success-icon" viewBox="0 0 24 24" width="20" height="20">
                <path fill="currentColor" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
            </svg>
            <span>${message}</span>
        `;
        
        this.formElement.insertBefore(success, this.formElement.firstChild);
        this.formElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        
        // Mark form as submitted
        this.formElement.dataset.submitted = 'true';
    }

    showError(message) {
        this.removeMessages();
        
        const error = document.createElement('div');
        error.className = 'error-message';
        error.setAttribute('role', 'alert');
        error.innerHTML = `
            <svg class="error-icon" viewBox="0 0 24 24" width="20" height="20">
                <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
            </svg>
            <span>${message}</span>
        `;
        
        this.formElement.insertBefore(error, this.formElement.firstChild);
        this.formElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        
        // Announce to screen readers
        this.errorAnnouncer.textContent = message;
    }

    showFieldError(field, message) {
        this.clearFieldError(field);
        
        field.classList.add('error');
        field.setAttribute('aria-invalid', 'true');
        field.setAttribute('aria-describedby', `${field.name}-error`);
        
        const errorElement = document.createElement('span');
        errorElement.id = `${field.name}-error`;
        errorElement.className = 'field-error';
        errorElement.textContent = message;
        errorElement.setAttribute('role', 'alert');
        
        field.parentElement.appendChild(errorElement);
    }

    clearFieldError(field) {
        field.classList.remove('error');
        field.removeAttribute('aria-invalid');
        field.removeAttribute('aria-describedby');
        
        const existingError = field.parentElement.querySelector('.field-error');
        if (existingError) {
            existingError.remove();
        }
    }

    removeMessages() {
        this.formElement.querySelectorAll('.success-message, .error-message').forEach(el => el.remove());
        this.errorAnnouncer.textContent = '';
    }

    disableForm() {
        this.formElement.querySelectorAll('input, textarea, select, button').forEach(el => {
            el.disabled = true;
        });
    }

    // Validation methods
    validateField(field) {
        // Skip honeypot
        if (field.name === 'website') return true;
        
        const value = field.value.trim();
        let error = null;
        
        // Required validation
        if (field.required && !value) {
            error = this.getFieldError(field);
        }
        // Type-specific validation
        else if (value) {
            switch (field.type) {
                case 'email':
                    if (!this.isValidEmail(value)) {
                        error = 'Please enter a valid email address';
                    }
                    break;
                case 'tel':
                    if (!this.isValidPhone(value)) {
                        error = 'Please enter a valid phone number';
                    }
                    break;
            }
            
            // Custom validation
            if (!error && field.dataset.validate) {
                error = this.customValidation(field, value);
            }
        }
        
        if (error) {
            this.showFieldError(field, error);
            return false;
        } else {
            this.clearFieldError(field);
            field.classList.add('valid');
            return true;
        }
    }

    isValidEmail(email) {
        // RFC 5322 compliant email regex
        const emailRegex = /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
        return emailRegex.test(email) && email.length <= 254;
    }

    isValidPhone(phone) {
        // Remove all non-digits for validation
        const digits = phone.replace(/\D/g, '');
        return digits.length >= 10 && digits.length <= 15;
    }

    customValidation(field, value) {
        // Add custom validation rules here
        switch (field.dataset.validate) {
            case 'no-spam':
                if (this.containsSpamKeywords(value)) {
                    return 'Please remove promotional content';
                }
                break;
            case 'min-words':
                const minWords = parseInt(field.dataset.minWords) || 5;
                if (value.split(/\s+/).length < minWords) {
                    return `Please enter at least ${minWords} words`;
                }
                break;
        }
        return null;
    }

    containsSpamKeywords(text) {
        const spamKeywords = ['viagra', 'cialis', 'casino', 'lottery', 'winner', 'click here', 'buy now'];
        const lowerText = text.toLowerCase();
        return spamKeywords.some(keyword => lowerText.includes(keyword));
    }

    getFieldError(field) {
        const label = field.labels?.[0]?.textContent || field.placeholder || field.name;
        
        if (field.validity.valueMissing) {
            return `${label} is required`;
        }
        if (field.validity.typeMismatch) {
            return `Please enter a valid ${field.type}`;
        }
        if (field.validity.tooShort) {
            return `${label} must be at least ${field.minLength} characters`;
        }
        if (field.validity.tooLong) {
            return `${label} must be less than ${field.maxLength} characters`;
        }
        if (field.validity.patternMismatch) {
            return field.dataset.patternError || `Please match the required format`;
        }
        
        return 'Please correct this field';
    }

    // Utility methods
    checkRateLimit() {
        const now = Date.now();
        const timeSinceLastSubmit = now - this.lastSubmitTime;
        
        // Allow first submission immediately
        if (this.lastSubmitTime === 0) {
            this.lastSubmitTime = now;
            return true;
        }
        
        // Require 5 seconds between submissions
        if (timeSinceLastSubmit < 5000) {
            this.rateLimitCount++;
            return false;
        }
        
        this.lastSubmitTime = now;
        this.rateLimitCount = 0;
        return true;
    }

    hasFormData() {
        const fields = this.formElement.querySelectorAll('input:not([name="website"]), textarea');
        return Array.from(fields).some(field => field.value.trim().length > 0);
    }

    getFormData() {
        const formData = {};
        const fields = this.formElement.querySelectorAll('input:not([name="website"]), textarea, select');
        
        fields.forEach(field => {
            if (field.name) {
                formData[field.name] = field.value.trim();
            }
        });
        
        return formData;
    }

    debounce(key, func, delay) {
        clearTimeout(this.debounceTimers.get(key));
        this.debounceTimers.set(key, setTimeout(func, delay));
    }

    // Public API for external access
    getFormInsights() {
        const data = this.getFormCompletionData();
        const insights = {
            formStarted: this.formStarted,
            timeSpent: this.getTimeSpent(),
            progress: data.progress,
            completedFields: data.completedFields,
            totalFields: data.totalFields,
            fieldsWithErrors: data.fieldsWithErrors,
            averageTimePerField: data.averageTimePerField,
            lastInteractedField: this.lastInteractedField,
            deviceType: this.getDeviceType(),
            abandonmentRisk: this.calculateAbandonmentRisk(data)
        };
        
        return insights;
    }

    calculateAbandonmentRisk(data) {
        // Calculate risk score based on multiple factors
        let risk = 0;
        
        // Long time on form
        if (this.getTimeSpent() > 300) risk += 2; // Over 5 minutes
        
        // Many errors
        if (data.fieldsWithErrors > 2) risk += 3;
        
        // Low progress
        if (data.progress < 50 && this.getTimeSpent() > 60) risk += 2;
        
        // High field revisits
        const avgRevisits = data.totalInteractions / Math.max(data.fieldsWithInput, 1);
        if (avgRevisits > 3) risk += 2;
        
        // Return risk level
        if (risk >= 6) return 'high';
        if (risk >= 3) return 'medium';
        return 'low';
    }
}

// Export for use in other modules
export default ContactForm;